import { Search } from 'lucide-react';
import {
  TASK_DUE_BUCKETS,
  TASK_DUE_BUCKET_LABELS,
  type TaskDueBucket,
  type TaskStatusFilter,
} from '@peles/shared';
import { FilterSelect } from '../common/FilterSelect';
import { SegmentedControl } from '../common/SegmentedControl';
import type { TaskAssignee } from './taskUi';

export interface TaskFilters {
  q: string;
  assignee: string[];
  due: TaskDueBucket[];
  status: TaskStatusFilter;
}

export const EMPTY_TASK_FILTERS: TaskFilters = { q: '', assignee: [], due: [], status: 'open' };

export interface TaskFilterBarProps {
  value: TaskFilters;
  onChange: (value: TaskFilters) => void;
  assignees: TaskAssignee[];
  /** Due filters make no sense on a list view that is not grouped by date. */
  showDue?: boolean;
  showStatus?: boolean;
}

const STATUS_OPTIONS: { id: TaskStatusFilter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'done', label: 'Done' },
  { id: 'all', label: 'All' },
];

export function TaskFilterBar({
  value,
  onChange,
  assignees,
  showDue = true,
  showStatus = true,
}: TaskFilterBarProps) {
  const people = [{ id: 'me', name: 'Me' }, { id: 'unassigned', name: 'Unassigned' }, ...assignees];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="relative flex-1 min-w-[12rem] max-w-sm">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={value.q}
          onChange={(e) => onChange({ ...value, q: e.target.value })}
          placeholder="Search tasks"
          aria-label="Search tasks"
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </label>
      <FilterSelect
        label="Assigned to"
        items={people}
        value={value.assignee}
        onChange={(v) => onChange({ ...value, assignee: Array.isArray(v) ? v : [v] })}
        getValue={(p) => p.id}
        getLabel={(p) => p.name}
        multiple
        searchable
      />
      {showDue && (
        <FilterSelect
          label="Due"
          items={TASK_DUE_BUCKETS.map((id) => ({ id, label: TASK_DUE_BUCKET_LABELS[id] }))}
          value={value.due}
          onChange={(v) =>
            onChange({ ...value, due: (Array.isArray(v) ? v : [v]) as TaskDueBucket[] })
          }
          getValue={(b) => b.id}
          getLabel={(b) => b.label}
          multiple
        />
      )}
      {showStatus && (
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={value.status}
          onChange={(status) => onChange({ ...value, status })}
          ariaLabel="Show tasks"
          variant="chips"
        />
      )}
    </div>
  );
}
